/**
 * CR229: T3 - Missing Template Banner
 * 
 * Shows a warning when a deal type has no custom template
 * and is using the DEFAULT_GENERIC fallback.
 * Provides one-click template creation.
 */

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle, FilePlus } from "lucide-react";

interface MissingTemplateBannerProps {
  dealTypeKey: string;
  fallbackFrom: string;
  onCreateTemplate: () => void;
  isCreating?: boolean;
}

export function MissingTemplateBanner({
  dealTypeKey,
  fallbackFrom,
  onCreateTemplate,
  isCreating = false,
}: MissingTemplateBannerProps) {
  return (
    <Alert variant="default" className="border-amber-200 bg-amber-50">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertTitle className="text-amber-800">No template exists for {dealTypeKey}</AlertTitle>
      <AlertDescription className="text-amber-700">
        <p className="mb-3">
          Using <strong>{fallbackFrom}</strong> as fallback. Create a custom template to configure 
          sections, labels, and styling specific to {dealTypeKey} deals.
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={onCreateTemplate}
          disabled={isCreating}
          className="gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-100"
        >
          <FilePlus className="h-4 w-4" />
          {isCreating ? "Creating..." : `Create template from ${fallbackFrom}`}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
